import React, { useState } from "react";
import { useNavigation } from "@react-navigation/native";
import { getBaseUrl } from "../../config";
import { getMyTasselPublicUrls } from "../../services/VanitySlugService";
import { SPOTLIGHT_COLORS, SPOTLIGHT_FONT_FAMILY, SPOTLIGHT_Z_INDEX, ensureSpotlightStyles } from "./spotlightTheme";

const COPY_STATUS = { IDLE: "idle", COPIED: "copied", FAILED: "failed" };

const styles = {
	scrim: {
		position: "fixed",
		inset: 0,
		zIndex: SPOTLIGHT_Z_INDEX,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		padding: 16,
		boxSizing: "border-box",
		backgroundColor: SPOTLIGHT_COLORS.scrim,
		animation: "spotlightFadeIn 240ms ease both",
	},
	card: {
		boxSizing: "border-box",
		width: "100%",
		maxWidth: 460,
		maxHeight: "100%",
		overflowY: "auto",
		padding: "32px 28px 22px",
		textAlign: "center",
		backgroundColor: SPOTLIGHT_COLORS.cream,
		color: SPOTLIGHT_COLORS.text,
		borderRadius: 24,
		boxShadow: "0 24px 64px rgba(10, 24, 16, 0.5), 0 0 0 1px rgba(76, 149, 108, 0.3)",
		fontFamily: SPOTLIGHT_FONT_FAMILY,
		animation: "spotlightCardIn 360ms ease both",
	},
	title: { margin: "0 0 10px", fontSize: 28, lineHeight: 1.2, color: SPOTLIGHT_COLORS.greenDark, fontWeight: 700 },
	body: { margin: "0 0 18px", fontSize: 16, lineHeight: 1.5 },
	linkBox: {
		margin: "0 0 14px",
		padding: "10px 12px",
		borderRadius: 12,
		backgroundColor: SPOTLIGHT_COLORS.greenLight,
		color: SPOTLIGHT_COLORS.greenDark,
		fontSize: 14,
		fontWeight: 700,
		wordBreak: "break-all",
	},
	primaryButton: {
		width: "100%",
		padding: "14px 20px",
		border: "none",
		borderRadius: 999,
		backgroundColor: SPOTLIGHT_COLORS.green,
		color: SPOTLIGHT_COLORS.white,
		fontFamily: SPOTLIGHT_FONT_FAMILY,
		fontSize: 17,
		fontWeight: 700,
	},
	copyMessage: { minHeight: 40, margin: "10px 0 14px", fontSize: 14, lineHeight: 1.4, color: SPOTLIGHT_COLORS.mutedText },
	copyMessageSuccess: { color: SPOTLIGHT_COLORS.greenDark },
	copyMessageError: { color: SPOTLIGHT_COLORS.orange },
	secondaryRow: { display: "flex", flexWrap: "wrap", gap: 10 },
	secondaryButton: {
		flex: "1 1 160px",
		padding: "11px 16px",
		border: `1px solid ${SPOTLIGHT_COLORS.green}`,
		borderRadius: 999,
		backgroundColor: "transparent",
		color: SPOTLIGHT_COLORS.greenDark,
		fontFamily: SPOTLIGHT_FONT_FAMILY,
		fontSize: 15,
		fontWeight: 700,
	},
	closeLink: {
		marginTop: 14,
		padding: 4,
		border: "none",
		background: "none",
		fontFamily: SPOTLIGHT_FONT_FAMILY,
		fontSize: 13,
		color: SPOTLIGHT_COLORS.mutedText,
		textDecoration: "underline",
	},
};

const COPY_MESSAGES = {
	[COPY_STATUS.IDLE]: "Copies your page link so you can paste it into Messages, email, social media, or anywhere you like.",
	[COPY_STATUS.COPIED]: "Link copied! Paste it into Messages, email, social media, or anywhere you like.",
	[COPY_STATUS.FAILED]: "We couldn't copy the link automatically. Select the link above and copy it.",
};

const COPY_MESSAGE_STYLES = {
	[COPY_STATUS.IDLE]: null,
	[COPY_STATUS.COPIED]: styles.copyMessageSuccess,
	[COPY_STATUS.FAILED]: styles.copyMessageError,
};

const useCopyToClipboard = (text) => {
	const [status, setStatus] = useState(COPY_STATUS.IDLE);
	const copy = async () => {
		try {
			await navigator.clipboard.writeText(text);
			setStatus(COPY_STATUS.COPIED);
		} catch (error) {
			console.error("Failed to copy the page link:", error);
			setStatus(COPY_STATUS.FAILED);
		}
	};
	return { status, copy };
};

/**
 * The last thing a new graduate sees: a celebratory card with quick ways to share, preview and leave.
 * Onboarding is already marked complete by the time this shows, so every button here is safe to use.
 */
const SpotlightCompletionCard = ({ step, userData, onClose }) => {
	ensureSpotlightStyles();
	const navigation = useNavigation();
	const pageUrl = getMyTasselPublicUrls({ pageOwner: userData, pageOwnerId: userData?.uid, baseUrl: getBaseUrl() }).announcement;
	const { status, copy } = useCopyToClipboard(pageUrl);

	const leaveTo = (screenName, params) => {
		onClose();
		navigation.navigate(screenName, params);
	};

	return (
		<div style={styles.scrim} data-spotlight-root>
			<div style={styles.card} role="dialog" aria-label={step.title} data-spotlight-animated>
				<h2 style={styles.title}>{step.title}</h2>
				<p style={styles.body}>{step.body}</p>
				<p style={styles.linkBox}>{pageUrl}</p>
				<button type="button" className="spotlight-button" style={styles.primaryButton} onClick={copy}>
					Share My Page
				</button>
				<p style={{ ...styles.copyMessage, ...COPY_MESSAGE_STYLES[status] }} role="status" aria-live="polite">
					{COPY_MESSAGES[status]}
				</p>
				<div style={styles.secondaryRow}>
					<button type="button" className="spotlight-button" style={styles.secondaryButton} onClick={() => leaveTo("Announcements", { uid: userData?.uid, viewAsVisitor: true })}>
						Preview My Page
					</button>
					<button type="button" className="spotlight-button" style={styles.secondaryButton} onClick={() => leaveTo("Hub")}>
						Go to My Hub
					</button>
				</div>
				<button type="button" className="spotlight-link" style={styles.closeLink} onClick={onClose}>
					Close
				</button>
			</div>
		</div>
	);
};

export default SpotlightCompletionCard;
